import type { ReactNode } from 'react';
import { Check, Lock, Repeat } from 'lucide-react';
import { addDays, deadlineStatus, groupByDay, isoWeek, startDay, type DisplayItem, type ISODate } from '@autoday/core';
import { ItemIcon } from '../ui/icons.tsx';
import { Badge } from '../ui/kit.tsx';
import { catColor, itemAriaLabel, timeLabel } from '../ui/format.ts';
import { useUi } from '../App.tsx';
import { useStore } from '../store/appStore.ts';
import { tr } from '../i18n/i18n.ts';
import { calendarName, deadlineLabel, formatDateLong, formatDayMonth, tagExplanation } from '../i18n/format.ts';

export function TagBadge({ item }: { item: Pick<DisplayItem, 'tag'> }) {
  if (!item.tag) return null;
  // Taggen är källans egen text (t.ex. [MÅSTE]) och visas som den står; förklaringen följer språket.
  const label = item.tag.tag ?? item.tag.rawPrefix;
  if (!label) return null;
  return (
    <Badge tone={item.tag.tag === '[MÅSTE]' || item.tag.tag === '[FÖRBEREDELSE]' ? 'source' : 'neutral'} title={tagExplanation(item.tag)}>
      {label}
      <span className="sr-only"> – {tagExplanation(item.tag)}</span>
    </Badge>
  );
}

export function StatusBadge({ item, today, tz }: { item: DisplayItem; today: ISODate; tz: string }) {
  if (!item.locked && !item.linkedSourceKey && item.priority !== 'high') return item.done ? <Badge tone="ok">{tr('Klar', 'Done')}</Badge> : null;
  const st = deadlineStatus(startDay(item.timing, tz), today, item.done);
  const tone = st.urgency === 'done' ? 'ok' : st.urgency === 'passed' || st.urgency === 'today' || st.urgency === 'near' ? 'risk' : 'neutral';
  return <Badge tone={tone}>{deadlineLabel(st)}</Badge>;
}

/** Kalenderns namn på valt språk (standardnamn översätts bara om de är orörda). */
export function useCalendarLabel(): (item: Pick<DisplayItem, 'calendarId' | 'calendarName'>) => string {
  const { state } = useStore();
  return (item) => calendarName(state.calendars[item.calendarId], item.calendarName);
}

export function AgendaRow({ item, today, tz, showDate = false, extra, bare = false }: { item: DisplayItem; today: ISODate; tz: string; showDate?: boolean; extra?: ReactNode; bare?: boolean }) {
  const ui = useUi();
  const calLabel = useCalendarLabel()(item);
  const button = (
      <button
        onClick={() => ui.openItem(item)}
        aria-label={itemAriaLabel(item, tz, calLabel)}
        className="group flex min-h-14 w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-raised"
      >
        <span className="w-24 shrink-0 pt-0.5 text-sm tabular-nums text-muted">{showDate ? formatDayMonth(startDay(item.timing, tz)) : timeLabel(item.timing, tz)}</span>
        <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md border border-border-strong" style={{ color: catColor(item.color) }}>
          <ItemIcon icon={item.icon} size={16} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block truncate text-sm font-medium ${item.done ? 'text-muted line-through' : 'text-text'}`}>{item.title}</span>
          <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted">
            <TagBadge item={item} />
            <StatusBadge item={item} today={today} tz={tz} />
            {item.locked && (
              <span className="inline-flex items-center gap-1" title={tr('Importerat källdatum – kan inte flyttas', 'Imported source date – cannot be moved')}>
                <Lock size={12} aria-hidden />
                {calLabel}
              </span>
            )}
            {!item.locked && <span>{calLabel}</span>}
            {item.location && <span className="truncate">· {item.location}</span>}
            {item.recurring && <Repeat size={12} aria-label={tr('Återkommande', 'Recurring')} />}
            {item.done && <Check size={12} aria-label={tr('Klar', 'Done')} className="text-ok" />}
            {item.review && <Badge tone="warn">{tr('Granska', 'Review')}</Badge>}
            {item.missingInLatest && <Badge tone="warn">{tr('Saknas i senaste import', 'Missing in latest import')}</Badge>}
            {item.adjusted && (
              <Badge tone="info" title={tr('Tiden fanns inte p.g.a. sommartid och flyttades fram', 'The time did not exist because of daylight saving and was moved forward')}>
                {tr('Sommartidsjusterad', 'Adjusted for DST')}
              </Badge>
            )}
            {extra}
          </span>
        </span>
      </button>
  );
  // bare: bara knappen, för listor som lägger egna åtgärder under raden (Viktigt).
  return bare ? button : <li>{button}</li>;
}

/** Agenda: dag för dag. Tomma dagar hoppas över men "idag" visas alltid. */
export function AgendaList({ items, from, days, today, tz, emptyText }: { items: DisplayItem[]; from: ISODate; days: number; today: ISODate; tz: string; emptyText?: string }) {
  const groups: Array<{ day: ISODate; items: DisplayItem[] }> = [];
  const byDay = groupByDay(items, from, days, tz);
  for (let i = 0; i < days; i++) {
    const d = addDays(from, i);
    const its = byDay.get(d) ?? [];
    if (its.length || d === today) groups.push({ day: d, items: its });
  }
  if (!groups.some((g) => g.items.length)) return <p className="px-3 py-6 text-sm text-muted">{emptyText ?? tr('Inga aktiviteter i perioden.', 'No activities in this period.')}</p>;
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {groups.map((g) => (
        <section key={g.day} aria-labelledby={`ag-${g.day}`}>
          <h3 id={`ag-${g.day}`} className="sticky top-0 z-10 flex items-baseline gap-2 border-b border-border bg-bg/95 px-3 py-2 text-sm font-semibold backdrop-blur">
            <span className={g.day === today ? 'text-accent' : ''}>
              {g.day === today ? tr('Idag, ', 'Today, ') : ''}
              {formatDateLong(g.day, false)}
            </span>
            <span className="text-xs font-normal text-muted">{tr('v.', 'wk')} {isoWeek(g.day).week}</span>
          </h3>
          {g.items.length ? (
            <ul className="mt-1">
              {g.items.map((it) => (
                <AgendaRow key={it.uid} item={it} today={today} tz={tz} />
              ))}
            </ul>
          ) : (
            <p className="px-3 py-3 text-sm text-muted">{tr('Inget inplanerat.', 'Nothing planned.')}</p>
          )}
        </section>
      ))}
    </div>
  );
}
